// docs/69 — a unit's statuses on the board. Each status has a look that lives inside the unit's
// container (so it walks, lunges and is sorted with it): parts on the ground behind the figure,
// parts over it, a tinted copy of the body for the ones that change how it looks (burning, slowed,
// hidden…), and its badge in the row over the head with a pip for each turn it still holds.
// `set` draws the statuses a board carries; `apply` / `remove` follow a pulse as it plays (a new
// status lands with a ring and a burst in its colour). Reduce motion keeps every mark and drops the
// particles and the turning; the effect-intensity setting thins the particles.
import Phaser from 'phaser';
import { STATUS_INFO, shownStatuses } from '../../core/sim/statuses';
import type { StatusKind, StatusTag } from '../../core/sim/types';
import { useSettings } from '../../core/stores/settings';
import { TEX, ensureSpellTextures } from '../spells/textures';
import { faceOf } from '../hugeArt';
import { BADGE_PX, ST_TEX, badgeKey, ensureStatusTextures } from './textures';
import { KIT_HEAD } from '../heroKit';
import { oneShot } from '../board/oneShots';

type Obj = Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Transform & Phaser.GameObjects.Components.Visible;
type EmitCfg = Phaser.Types.GameObjects.Particles.ParticleEmitterConfig;
type Zone = Phaser.Types.GameObjects.Particles.RandomZoneSource;
const ADD = Phaser.BlendModes.ADD;
const NORMAL = Phaser.BlendModes.NORMAL;
/** Badges per row over the head; a fourth starts a second row above. */
const PER_ROW = 3;
/** The most turn pips a badge shows (a longer status still shows three). */
const MAX_PIPS = 3;

/** What the aura needs of its unit (UnitSprite): its container, body copies and the cell. */
export interface AuraHost {
  readonly scene: Phaser.Scene;
  readonly container: Phaser.GameObjects.Container;
  readonly body: Phaser.GameObjects.Image;
  /** The body and its outline copies (a sway turns them all). */
  readonly bodies: Phaser.GameObjects.Image[];
  readonly cell: number;
}

export interface AuraQuality { density: number; still: boolean }

export function auraQuality(): AuraQuality {
  const s = useSettings.getState();
  return { density: Math.max(0, Math.min(1, s.effectIntensityBp / 10000)), still: s.reduceMotion };
}

/** One status's look: its parts and what moves them each frame. */
interface Look {
  kind: StatusKind;
  born: number;
  parts: Obj[];
  /** `t` ms since the scene began, `age` ms since this look began. */
  tick?(t: number, age: number): void;
  /** Undo what it did to the body itself (a sway). */
  undo?(): void;
}

export class StatusAura {
  private looks = new Map<StatusKind, Look>();
  private tags: StatusTag[] = [];
  private badges: Obj[] = [];
  private q: AuraQuality;
  private readonly onUpdate = (t: number) => this.update(t);
  private readonly events: Phaser.Events.EventEmitter;
  private dead = false;

  constructor(private readonly host: AuraHost, quality: AuraQuality = auraQuality()) {
    this.q = quality;
    ensureSpellTextures(host.scene);
    ensureStatusTextures(host.scene);
    this.events = host.scene.events;
    this.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    host.container.once(Phaser.GameObjects.Events.DESTROY, () => this.destroy());
  }

  /** The statuses the unit carries now (from the board): looks for the new, the gone ones go. */
  set(tags: readonly StatusTag[] | undefined): void {
    this.tags = shownStatuses(tags);
    const want = new Set(this.tags.map((t) => t.kind));
    for (const k of [...this.looks.keys()]) if (!want.has(k)) this.drop(k);
    for (const t of this.tags) if (!this.looks.has(t.kind)) this.start(t.kind);
    this.drawBadges();
  }

  /** A pulse set a status as it played: it lands with a flash in its colour. */
  apply(kind: StatusKind, magnitude: number, pulses?: number): void {
    const had = this.tags.find((t) => t.kind === kind);
    if (had) { had.magnitude = Math.max(had.magnitude, magnitude); if (pulses !== undefined) had.pulses = Math.max(had.pulses ?? 0, pulses); }
    else this.tags = shownStatuses([...this.tags, { kind, magnitude, ...(pulses !== undefined ? { pulses } : {}) }]);
    if (!this.looks.has(kind)) this.start(kind);
    this.drawBadges();
    this.flash(kind);
  }

  /** A pulse cleared a status as it played. */
  remove(kind: StatusKind): void {
    this.tags = this.tags.filter((t) => t.kind !== kind);
    this.drop(kind);
    this.drawBadges();
  }

  /** Everything goes (it fell). */
  clear(): void { this.set([]); }

  /** The kinds drawn now (review drivers, tests). */
  drawn(): StatusKind[] { return [...this.looks.keys()]; }

  destroy(): void {
    if (this.dead) return;
    this.dead = true;
    this.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    for (const l of this.looks.values()) l.undo?.();
    this.looks.clear();
  }

  // ---- frame ------------------------------------------------------------------------------------

  private update(t: number): void {
    if (this.dead || !this.host.container.active) return;
    for (const l of this.looks.values()) l.tick?.(t, t - l.born);
  }

  // ---- badges -----------------------------------------------------------------------------------

  /** Lays the badges out again (a hero's kit look swapped in for its Oryx frame, docs/145 KH6). */
  relayout(): void { this.drawBadges(); }

  /** Where the badge row's bottom sits: just over a kit hero's head (under its canvas's headroom), else over the frame. */
  private headTop(): number {
    const { body } = this.host;
    if (body.texture.key.startsWith('hk:')) return Math.round(-body.originY * body.height + KIT_HEAD) - 1;
    return -Math.max(body.height, this.host.cell * 0.8) - 4;
  }

  private drawBadges(): void {
    for (const b of this.badges) b.destroy();
    this.badges = [];
    const { scene, container } = this.host;
    const n = this.tags.length;
    if (!n) return;
    const top = this.headTop();
    const stride = BADGE_PX + 1;
    this.tags.forEach((t, i) => {
      const row = Math.floor(i / PER_ROW), inRow = Math.min(PER_ROW, n - row * PER_ROW), col = i % PER_ROW;
      const x = Math.round(-((inRow - 1) * stride) / 2 + col * stride), y = Math.round(top - row * (stride + 2));
      const img = scene.add.image(x, y, badgeKey(t.kind)).setOrigin(0.5, 1);
      this.badges.push(img);
      container.add(img);
      // one pip under the badge for each turn it still holds
      const pips = Math.min(MAX_PIPS, t.pulses ?? 0);
      for (let p = 0; p < pips; p++) {
        const pip = scene.add.image(x - (pips - 1) * 1.5 + p * 3, y + 1, ST_TEX.pip).setOrigin(0.5, 0).setScale(0.5).setTint(STATUS_INFO[t.kind].colour);
        this.badges.push(pip);
        container.add(pip);
      }
    });
  }

  // ---- looks ------------------------------------------------------------------------------------

  private start(kind: StatusKind): void {
    const look = this.build(kind);
    look.tick?.(this.host.scene.time.now, 0);
    this.looks.set(kind, look);
  }

  private drop(kind: StatusKind): void {
    const l = this.looks.get(kind);
    if (!l) return;
    this.looks.delete(kind);
    l.undo?.();
    for (const p of l.parts) {
      if (p instanceof Phaser.GameObjects.Particles.ParticleEmitter) { p.stop(); this.host.scene.time.delayedCall(1200, () => p.destroy()); }
      else p.destroy();
    }
  }

  /** A part on the ground, behind the figure (over the base marker). */
  private ground<T extends Obj>(o: T): T { this.host.container.addAt(o, 1); return o; }
  /** A part over the figure. */
  private front<T extends Obj>(o: T): T { this.host.container.add(o); return o; }

  private image(tex: string, colour: number, blend: Phaser.BlendModes = NORMAL): Phaser.GameObjects.Image {
    return this.host.scene.add.image(0, 0, tex).setTint(colour).setBlendMode(blend);
  }

  /** A copy of the body filled with `colour`, laid over it at `alpha` (synced each frame). */
  private veil(colour: number): Phaser.GameObjects.Image {
    const { body, container } = this.host;
    const v = this.host.scene.add.image(0, 0, body.texture.key, body.frame.name).setOrigin(0.5, 1).setTint(colour).setTintMode(Phaser.TintModes.FILL).setAlpha(0);
    container.addAt(v, container.getIndex(body) + 1);
    return v;
  }

  private syncVeil(v: Phaser.GameObjects.Image, alpha: number, dx = 0): void {
    const b = this.host.body;
    if (v.texture.key !== b.texture.key || v.frame.name !== b.frame.name) v.setTexture(b.texture.key, b.frame.name);
    v.setPosition(b.x + dx, b.y).setFlipX(b.flipX).setAngle(b.angle).setScale(b.scaleX, b.scaleY).setAlpha(alpha * b.alpha).setVisible(b.visible);
    // docs/93 AQ10: a figure cut at the waterline (WaterLine) is veiled only above it
    const crop = b.getData('fkCropH') as number | undefined;
    if (v.originY !== b.originY) v.setOrigin(b.originX, b.originY);
    if (crop) v.setCrop(0, 0, v.frame.width, crop); else if (v.isCropped) v.setCrop();
  }

  /** A looping emitter inside the container, thinned by the effect intensity; none with reduce motion. */
  private emitter(tex: string, cfg: EmitCfg, x = 0, y = 0): Phaser.GameObjects.Particles.ParticleEmitter | undefined {
    if (this.q.still || this.q.density < 0.15) return undefined;
    const f = typeof cfg.frequency === 'number' ? cfg.frequency / Math.max(0.3, this.q.density) : cfg.frequency;
    const e = this.host.scene.add.particles(x, y, tex, { ...cfg, ...(f !== undefined ? { frequency: f } : {}) });
    this.host.container.add(e);
    return e;
  }

  /** The figure's height and its centre (feet are y = 0). */
  private get h(): number { return Math.max(this.host.body.height, this.host.cell * 0.7); }

  private build(kind: StatusKind): Look {
    const { cell } = this.host;
    const h = this.h, still = this.q.still, born = this.host.scene.time.now;
    const colour = STATUS_INFO[kind].colour;
    const look: Look = { kind, born, parts: [] };
    const keep = <T extends Obj | undefined>(o: T): T => { if (o) look.parts.push(o); return o; };
    const rect = (x: number, y: number, w: number, hh: number) => ({ type: 'random' as const, source: new Phaser.Geom.Rectangle(x, y, w, hh) as unknown as Zone });
    const facing = () => faceOf(this.host.body);

    switch (kind) {
      case 'Guarded': {
        // a steel shield held up before it, shimmering, a glint running down it now and then
        const s = keep(this.front(this.image(TEX.shield, colour)).setScale((cell * 0.5) / 28).setOrigin(0.5, 0.5));
        const glint = keep(this.front(this.image(TEX.px, 0xffffff, ADD)).setScale(1));
        look.tick = (t) => {
          const x = facing() * cell * 0.3, y = -h * 0.45;
          s.setPosition(x, y).setAlpha(still ? 0.75 : 0.6 + 0.2 * Math.sin(t / 380));
          const k = still ? -1 : ((t % 2600) / 700);
          glint.setVisible(k >= 0 && k <= 1);
          if (k >= 0 && k <= 1) glint.setPosition(x - cell * 0.12 + k * cell * 0.24, y - cell * 0.2 + k * cell * 0.4).setAlpha(1 - Math.abs(k - 0.5) * 2);
        };
        break;
      }
      case 'Warded': {
        // a rune circle turning flat under the feet, a soft bubble, three motes in orbit
        const disc = keep(this.ground(this.host.scene.add.container(0, -1).setScale(1, 0.38)));
        const rune = this.image(TEX.rune, colour, ADD).setScale((cell * 1.05) / 64).setAlpha(0.85);
        disc.add(rune);
        const bubble = keep(this.front(this.image(TEX.glow, colour, ADD)).setScale((cell * 1.1) / 64, (h * 1.25) / 64).setAlpha(0.16));
        const motes = [0, 1, 2].map(() => keep(this.front(this.image(TEX.dot, 0xdff6ff, ADD))));
        look.tick = (t) => {
          rune.setAngle(still ? 0 : t / 30);
          bubble.setPosition(0, -h * 0.5).setAlpha(0.13 + (still ? 0 : 0.05 * Math.sin(t / 300)));
          motes.forEach((m, i) => {
            const a = (still ? 0 : t / 700) + (i * Math.PI * 2) / 3;
            m.setPosition(Math.cos(a) * cell * 0.42, -h * (0.35 + 0.2 * i / 2) + Math.sin(a) * cell * 0.12).setAlpha(Math.sin(a) > 0 ? 1 : 0.35);
          });
        };
        break;
      }
      case 'Blessed': {
        // a golden halo over the head, bobbing, and motes of light rising off it
        const halo = keep(this.front(this.image(ST_TEX.halo, colour, ADD)).setScale((cell * 0.5) / 32, (cell * 0.16) / 32));
        keep(this.emitter(TEX.px, { lifespan: { min: 700, max: 1200 }, speedY: { min: -16, max: -7 }, speedX: { min: -3, max: 3 }, scale: { start: 1, end: 0 }, alpha: { start: 1, end: 0 },
          color: [0xfff6c8, 0xffd34d, 0xe09a20], blendMode: ADD, frequency: 140, emitZone: rect(-cell * 0.3, -h * 0.85, cell * 0.6, h * 0.8) }));
        look.tick = (t) => halo.setPosition(0, -h + 2 + (still ? 0 : Math.sin(t / 420))).setAlpha(still ? 0.9 : 0.75 + 0.2 * Math.sin(t / 260));
        break;
      }
      case 'Marked': {
        // a red reticle over the chest, turning, closing in and out
        const r = keep(this.front(this.image(TEX.reticle, colour)));
        const base = (cell * 0.9) / 48;
        look.tick = (t) => {
          const k = still ? 1 : 1 + 0.08 * Math.sin(t / 220);
          r.setPosition(0, -h * 0.5).setAngle(still ? 0 : t / 40).setScale(base * k).setAlpha(0.85);
        };
        break;
      }
      case 'Hidden': {
        // a shimmering veil over the figure, wavering a pixel, and smoke curling at its feet
        const v = keep(this.veil(0xd4def5));
        keep(this.emitter(TEX.smoke, { lifespan: { min: 900, max: 1500 }, speedY: { min: -7, max: -2 }, speedX: { min: -6, max: 6 }, scale: { start: 0.35, end: 0.9 }, alpha: { start: 0.42, end: 0 },
          tint: [0x9aa6c4, 0x6c7894], blendMode: NORMAL, frequency: 170, emitZone: rect(-cell * 0.4, -cell * 0.15, cell * 0.8, cell * 0.15) }));
        look.tick = (t) => this.syncVeil(v, still ? 0.5 : 0.45 + 0.15 * Math.sin(t / 170), still ? 0 : Math.round(Math.sin(t / 90) * 0.6));
        break;
      }
      case 'Slowed': {
        // tinted cold blue, slow ripples spreading from the feet, a few flakes drifting down
        const v = keep(this.veil(colour));
        const ripple = keep(this.ground(this.host.scene.add.graphics()));
        keep(this.emitter(TEX.flake, { lifespan: 1600, speedY: { min: 5, max: 9 }, speedX: { min: -2, max: 2 }, scale: { start: 0.7, end: 0.3 }, alpha: { start: 0.9, end: 0 },
          tint: [0xc8d4ff, 0x8fa4ff], blendMode: NORMAL, rotate: { min: 0, max: 360 }, frequency: 420, emitZone: rect(-cell * 0.4, -h * 1.05, cell * 0.8, h * 0.3) }));
        look.tick = (t) => {
          this.syncVeil(v, 0.38);
          ripple.clear();
          for (let i = 0; i < 2; i++) {
            const k = still ? 0.6 + i * 0.3 : ((t / 2400) + i / 2) % 1;
            ripple.lineStyle(1, colour, 0.8 * (1 - k)).strokeEllipse(0, -1, cell * (0.4 + 0.7 * k), cell * (0.14 + 0.24 * k));
          }
        };
        break;
      }
      case 'Rooted': {
        // roots coil up out of the ground round its legs, swaying
        const g = keep(this.front(this.host.scene.add.graphics()));
        const vines = [-0.34, -0.16, 0.02, 0.2, 0.34].map((x, i) => ({ x: x * cell, top: (0.32 + (i % 2) * 0.12) * cell, bend: (i % 2 ? 1 : -1) * cell * 0.12 }));
        look.tick = (t, age) => {
          const grow = still ? 1 : Math.min(1, age / 450);
          g.clear();
          for (const [i, v] of vines.entries()) {
            const sway = still ? 0 : Math.sin(t / 500 + i) * 1.2;
            const top = -v.top * grow, mid = top / 2;
            const p = new Phaser.Curves.QuadraticBezier(new Phaser.Math.Vector2(v.x, 0), new Phaser.Math.Vector2(v.x + v.bend + sway, mid), new Phaser.Math.Vector2(v.x - v.bend * 0.5 + sway, top));
            g.lineStyle(2, 0x2f4a1a, 1); p.draw(g, 8);
            g.lineStyle(1, 0x7fcf4e, 1); p.draw(g, 8);
            const tip = p.getPoint(1);
            if (grow > 0.8) { g.fillStyle(0x9be06a, 1); g.fillRect(Math.round(tip.x) - (i % 2 ? 0 : 2), Math.round(tip.y), 2, 1); }
          }
        };
        break;
      }
      case 'Burning': {
        // flames lick up the body (drawn NORMAL with a saturated ramp, so they hold on snow), embers fly, the body flickers orange
        const v = keep(this.veil(0xff7a1a));
        keep(this.emitter(TEX.flame, { lifespan: { min: 380, max: 680 }, speedY: { min: -34, max: -16 }, speedX: { min: -4, max: 4 }, scale: { start: 1.1, end: 0.3 }, alpha: { start: 1, end: 0.2 },
          color: [0xfff1a0, 0xffb02e, 0xf05a14, 0x8a1a0a], colorEase: 'Quad.easeOut', blendMode: NORMAL, frequency: 55, emitZone: rect(-cell * 0.28, -h * 0.75, cell * 0.56, h * 0.7) }));
        keep(this.emitter(TEX.px, { lifespan: { min: 500, max: 900 }, speedY: { min: -40, max: -18 }, speedX: { min: -10, max: 10 }, scale: { start: 0.8, end: 0 }, color: [0xffffff, 0xffc04a, 0xff5a1a], blendMode: ADD, frequency: 150,
          emitZone: rect(-cell * 0.25, -h * 0.9, cell * 0.5, h * 0.5) }));
        look.tick = (t) => this.syncVeil(v, still ? 0.18 : 0.12 + 0.1 * (0.5 + 0.5 * Math.sin(t / 70) * Math.sin(t / 113)));
        break;
      }
      case 'Weakened': {
        // washed grey-violet, a pall sinking off it to the ground
        const v = keep(this.veil(0x6e5a8a));
        keep(this.emitter(TEX.smoke, { lifespan: { min: 900, max: 1300 }, speedY: { min: 4, max: 10 }, speedX: { min: -3, max: 3 }, scale: { start: 0.5, end: 0.2 }, alpha: { start: 0.45, end: 0 },
          tint: [0x5a4672, 0x3a2c4c], blendMode: NORMAL, frequency: 210, emitZone: rect(-cell * 0.3, -h * 0.85, cell * 0.6, h * 0.5) }));
        look.tick = (t) => this.syncVeil(v, still ? 0.4 : 0.36 + 0.06 * Math.sin(t / 500));
        break;
      }
      case 'Staggered': {
        // stars circle the head, the figure sways on its feet
        const stars = [0, 1, 2].map((i) => keep(this.front(this.image(i === 1 ? TEX.dot : TEX.star, i === 1 ? 0xffffff : colour))));
        look.tick = (t) => {
          stars.forEach((s, i) => {
            const a = (still ? 0 : t / 260) + (i * Math.PI * 2) / 3;
            s.setPosition(Math.cos(a) * cell * 0.3, -h + 2 + Math.sin(a) * cell * 0.09).setAlpha(Math.sin(a) > -0.3 ? 1 : 0.45);
          });
          if (!still) for (const b of this.host.bodies) b.setAngle(Math.sin(t / 210) * 5);
        };
        look.undo = () => { for (const b of this.host.bodies) if (b.active) b.setAngle(0); };
        break;
      }
      case 'Stuck': {
        // a sticky web over the ground it stands on, goo dripping off its legs
        const disc = keep(this.ground(this.host.scene.add.container(0, -1).setScale(1, 0.42)));
        disc.add(this.image(ST_TEX.web, 0xe4f0c4).setScale((cell * 1.1) / 48).setAlpha(0.8));
        const strands = keep(this.front(this.host.scene.add.graphics()));
        strands.lineStyle(1, 0xe4f0c4, 0.7);
        for (const x of [-0.22, 0.05, 0.25]) strands.lineBetween(x * cell, 0, x * cell * 0.6, -h * 0.28);
        keep(this.emitter(TEX.drop, { lifespan: 700, speedY: { min: 2, max: 6 }, gravityY: 30, scale: { start: 0.8, end: 0.5 }, alpha: { start: 0.95, end: 0 },
          tint: [0xb6e05a, 0x8cbf2a], blendMode: NORMAL, frequency: 520, emitZone: rect(-cell * 0.25, -h * 0.35, cell * 0.5, h * 0.15) }));
        break;
      }
      case 'Bleeding': {
        // drops of blood fall from the body and a pool spreads at its feet
        const pool = keep(this.ground(this.image(ST_TEX.pool, 0x8a1020))).setOrigin(0.5, 0.5);
        keep(this.emitter(TEX.drop, { lifespan: 420, speedY: { min: 0, max: 6 }, speedX: { min: -2, max: 2 }, gravityY: 150, scale: { start: 1, end: 0.7 },
          color: [0xe0303f, 0xa01020, 0x6a0a14], blendMode: NORMAL, frequency: 300, emitZone: rect(-cell * 0.22, -h * 0.6, cell * 0.44, h * 0.2) }));
        look.tick = (_t, age) => {
          const k = still ? 1 : Math.min(1, 0.3 + age / 2200);
          pool.setPosition(cell * 0.06, -1).setScale((cell * 0.85 * k) / 32, (cell * 0.32 * k) / 16).setAlpha(0.8);
        };
        break;
      }
      case 'Downed': {
        // a faint heartbeat: two thumps of red spreading round it, then a rest
        const g = keep(this.ground(this.host.scene.add.graphics()));
        look.tick = (t) => {
          g.clear();
          const beats = still ? [0.5] : [((t % 1400) / 1400) * 2.2, (((t - 220) % 1400) / 1400) * 2.2];
          for (const k of beats) if (k >= 0 && k <= 1) g.lineStyle(1, 0xff6b6b, 0.7 * (1 - k)).strokeEllipse(0, -1, cell * (0.5 + 0.6 * k), cell * (0.18 + 0.2 * k));
        };
        break;
      }
      case 'Injured':
        // carried between fights: the badge says it; the figure is left alone
        break;
      case 'Frenzied': {
        // rage: a red pulse through the body and the ground, steam off the head
        const v = keep(this.veil(colour));
        const glow = keep(this.ground(this.image(TEX.glow, colour, ADD))).setScale((cell * 1.2) / 64, (cell * 0.45) / 64);
        keep(this.emitter(TEX.smoke, { lifespan: { min: 600, max: 900 }, speedY: { min: -22, max: -12 }, speedX: { min: -6, max: 6 }, scale: { start: 0.3, end: 0.8 }, alpha: { start: 0.55, end: 0 },
          tint: [0xffffff, 0xffb4a8], blendMode: NORMAL, frequency: 240, emitZone: rect(-cell * 0.18, -h - 1, cell * 0.36, 3) }));
        look.tick = (t) => {
          const beat = still ? 0.5 : Math.pow(Math.max(0, Math.sin(t / 150)), 4);
          this.syncVeil(v, 0.12 + 0.3 * beat);
          glow.setPosition(0, -1).setAlpha(0.25 + 0.35 * beat);
        };
        break;
      }
      case 'Arriving': {
        // a dashed amber ring turning at its feet, dust settling round it
        const g = keep(this.ground(this.host.scene.add.graphics()));
        keep(this.emitter(TEX.px, { lifespan: 900, speedY: { min: 6, max: 14 }, speedX: { min: -3, max: 3 }, alpha: { start: 0.9, end: 0 }, scale: { start: 0.8, end: 0.4 },
          tint: [0xffd2a0, colour], blendMode: NORMAL, frequency: 200, emitZone: rect(-cell * 0.45, -h * 1.1, cell * 0.9, h * 0.4) }));
        look.tick = (t) => {
          g.clear();
          const rr = cell / 2 - 1, spin = still ? 0 : t / 900;
          g.lineStyle(1, colour, 0.95);
          for (let a = 0; a < 12; a += 2) {
            const a0 = (a / 12) * Math.PI * 2 + spin, a1 = ((a + 1) / 12) * Math.PI * 2 + spin;
            g.lineBetween(Math.cos(a0) * rr, -1 + Math.sin(a0) * 3, Math.cos(a1) * rr, -1 + Math.sin(a1) * 3);
          }
        };
        break;
      }
      case 'Captive': {
        // bound with rope round its middle, straining against it now and then
        const g = keep(this.front(this.host.scene.add.graphics()));
        look.tick = (t) => {
          const strain = still ? 0 : (t % 2200) < 260 ? Math.sin(((t % 2200) / 260) * Math.PI * 3) : 0;
          g.clear().setPosition(strain, 0);
          for (const y of [-h * 0.38, -h * 0.55]) {
            g.lineStyle(2, 0x3a2410, 1).strokeEllipse(0, y, cell * 0.5, 4);
            g.lineStyle(1, 0xc8a066, 1).strokeEllipse(0, y, cell * 0.5, 4);
          }
          g.fillStyle(0xc8a066, 1).fillRect(cell * 0.18, -h * 0.47, 2, 3);
        };
        break;
      }
      case 'Shielded': {
        // docs/104 PD2: the paladin's shield of light stands at its back (Guarded's steel is in
        // front), glinting; a soft gold glow on it and a few motes rising
        const glow = keep(this.ground(this.image(TEX.glow, colour, ADD)).setScale((cell * 0.9) / 64, (cell * 0.5) / 64).setAlpha(0.35));
        const s = keep(this.front(this.image(ST_TEX.crest, colour)).setScale((cell * 0.42) / 14).setOrigin(0.5, 0.5));
        const shine = keep(this.front(this.image(ST_TEX.crest, 0xffffff, ADD)).setScale((cell * 0.42) / 14).setOrigin(0.5, 0.5));
        keep(this.emitter(TEX.px, { lifespan: { min: 600, max: 1000 }, speedY: { min: -14, max: -6 }, speedX: { min: -2, max: 2 }, scale: { start: 1, end: 0 }, alpha: { start: 1, end: 0 },
          color: [0xfffbe0, colour, 0xd08a1a], blendMode: ADD, frequency: 220, emitZone: rect(-cell * 0.35, -h * 0.7, cell * 0.7, h * 0.5) }));
        look.tick = (t) => {
          const x = -facing() * cell * 0.3, y = -h * 0.5;
          s.setPosition(x, y).setAlpha(0.9);
          shine.setPosition(x, y).setAlpha(still ? 0.15 : 0.1 + 0.25 * Math.max(0, Math.sin(t / 300)));
          glow.setPosition(0, -1).setAlpha(still ? 0.35 : 0.28 + 0.1 * Math.sin(t / 420));
        };
        break;
      }
      case 'Floundering': {
        // docs/93 AQ8: thrashing in the deep — splashes thrown up round it, rings spreading, bubbles
        // rising where it went under; the figure jerks side to side (its waterline bobs it, WaterLine)
        const rings = keep(this.front(this.host.scene.add.graphics()));
        keep(this.emitter(TEX.drop, { lifespan: { min: 420, max: 700 }, speed: { min: 30, max: 62 }, angle: { min: 215, max: 325 }, gravityY: 190, scale: { start: 1.3, end: 0.6 }, alpha: { start: 1, end: 0.25 },
          color: [0xffffff, 0xd6eeff, 0x8cc0ea], blendMode: NORMAL, frequency: 70, emitZone: rect(-cell * 0.32, -2, cell * 0.64, 3) }));
        keep(this.emitter(TEX.dot, { lifespan: { min: 600, max: 1000 }, speedY: { min: -9, max: -4 }, speedX: { min: -2, max: 2 }, scale: { start: 0.4, end: 0.8 }, alpha: { start: 0.9, end: 0 },
          tint: [0xe8f6ff, 0xa8d4f0], blendMode: NORMAL, frequency: 260, emitZone: rect(-cell * 0.35, -1, cell * 0.7, 2) }));
        look.tick = (t) => {
          rings.clear();
          for (let i = 0; i < 2; i++) {
            const k = still ? 0.5 + i * 0.3 : ((t / 900) + i / 2) % 1;
            rings.lineStyle(i === 0 ? 2 : 1, 0xffffff, 0.9 * (1 - k)).strokeEllipse(0, 0, cell * (0.6 + 0.8 * k), cell * (0.18 + 0.24 * k));
          }
          if (!still) for (const b of this.host.bodies) b.setAngle(Math.sin(t / 95) * 7 * Math.max(0, Math.sin(t / 430)));
        };
        look.undo = () => { for (const b of this.host.bodies) if (b.active) b.setAngle(0); };
        break;
      }
      case 'Surfaced': {
        // docs/93 AQ7: up out of the deep — water streams off it and drips back into the pool
        keep(this.emitter(TEX.drop, { lifespan: { min: 380, max: 640 }, speedY: { min: 4, max: 12 }, speedX: { min: -3, max: 3 }, gravityY: 140, scale: { start: 0.8, end: 0.5 }, alpha: { start: 0.95, end: 0.1 },
          color: [0xffffff, 0xcfe8fa, 0x8cc0ea], blendMode: NORMAL, frequency: 70, emitZone: rect(-cell * 0.3, -h * 0.9, cell * 0.6, h * 0.55) }));
        const sheen = keep(this.veil(0xbfe6ff));
        look.tick = (t, age) => this.syncVeil(sheen, still ? 0.15 : Math.max(0.08, 0.45 * (1 - age / 1200)) + 0.05 * Math.sin(t / 160));
        break;
      }
      case 'Raised': {
        // violet wisps of the raising rise off it; the figure carries a faint violet cast
        const v = keep(this.veil(colour));
        keep(this.emitter(TEX.smoke, { lifespan: { min: 900, max: 1400 }, speedY: { min: -14, max: -6 }, speedX: { min: -4, max: 4 }, scale: { start: 0.25, end: 0.6 }, alpha: { start: 0.6, end: 0 },
          tint: [0xc8a8ff, 0x8a5ad6], blendMode: NORMAL, frequency: 200, emitZone: rect(-cell * 0.28, -h * 0.8, cell * 0.56, h * 0.7) }));
        look.tick = (t) => this.syncVeil(v, still ? 0.22 : 0.18 + 0.08 * Math.sin(t / 400));
        break;
      }
    }
    return look;
  }

  /** A status lands: a ring in its colour opens round the figure and a burst of its sparks. */
  private flash(kind: StatusKind): void {
    const { scene, cell } = this.host;
    const colour = STATUS_INFO[kind].colour, h = this.h;
    const ring = oneShot(scene, this.front(this.image(TEX.ring, colour, ADD))).setPosition(0, -h * 0.5).setScale(0.15).setAlpha(1);
    scene.tweens.add({ targets: ring, scale: (cell * 1.4) / 48, alpha: 0, duration: this.q.still ? 1 : 420, ease: 'Quad.easeOut', onComplete: () => ring.destroy() });
    if (this.q.still || this.q.density < 0.15) return;
    const e = scene.add.particles(0, 0, TEX.star, { emitting: false, lifespan: 520, speed: { min: 18, max: 46 }, angle: { min: 0, max: 360 }, scale: { start: 0.9, end: 0 }, alpha: { start: 1, end: 0 },
      tint: [0xffffff, colour], blendMode: ADD });
    this.host.container.add(e);
    // one-shot emitters at the container's origin explode where they are told (docs/56: explode is emitter-relative)
    e.explode(Math.round(10 * this.q.density) + 2, 0, -h * 0.5);
    scene.time.delayedCall(700, () => e.destroy());
  }
}
